import React, { useState, useEffect, useLayoutEffect, useCallback } from "react";
import api from "../utils/api";

interface WithdrawalData {
  _id: string;
  fullName: string;
  email: string;
  mobileNumber: string;
  referralName: string;
  referralMail: string;
  joiningDate: string;
  investmentDate: string;
  totalAmount: number;
  withdrawalAmount: number;
  subscriptionPlan: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  finalAmountCredited?: number;
  daysSinceInvestment?: number;
  deductionPercentage?: number;
  breakdown?: Array<{
    amount: number;
    daysSince: number;
    deductionPercent: number;
    deductionAmount: number;
    investDate: string;
  }>;
}

const WithdrawalDataComponent: React.FC = () => {
  const [withdrawalData, setWithdrawalData] = useState<WithdrawalData[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<WithdrawalData | null>(null);
  const [actionType, setActionType] = useState<"Accept" | "Reject" | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useLayoutEffect(() => {
    setPage(1);
  }, [search, statusFilter, limit]);

  const fetchWithdrawals = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        search,
      });
      if (statusFilter !== "All") {
        params.set("status", statusFilter);
      }
      const response = await api.get(`/api/investmentwithdrawl/withdrawals?${params.toString()}`);
      if (response.data.status === "success") {
        setWithdrawalData(response.data.data);
        const p = response.data.pagination;
        if (p) {
          setTotal(p.total ?? 0);
          setTotalPages(Math.max(0, p.totalPages ?? 0));
        }
      }
    } catch (error) {
      console.error("Error fetching withdrawal data:", error);
      setWithdrawalData([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, search, statusFilter]);

  useEffect(() => {
    fetchWithdrawals();
  }, [fetchWithdrawals]);

  const handleAction = (item: WithdrawalData, action: "Accept" | "Reject") => {
    setSelectedWithdrawal(item);
    setActionType(action);
    setActionError(null);
    setShowPopup(true);
  };

  const handleConfirmAction = () => {
    if (!selectedWithdrawal || !actionType) return;
    const newStatus = actionType === "Accept" ? "approved" : "rejected";

    setActionError(null);
    api
      .patch(`/api/investmentwithdrawl/withdrawal/${selectedWithdrawal._id}`, {
        status: newStatus,
      })
      .then((response) => {
        setWithdrawalData((prevData) =>
          prevData.map((item) =>
            item._id === selectedWithdrawal._id
              ? { ...item, status: newStatus, ...response.data.data }
              : item
          )
        );
        setShowPopup(false);
        fetchWithdrawals();
      })
      .catch((error) => {
        const msg =
          (error?.response?.data?.message as string | undefined) ||
          (error?.response?.data?.error as string | undefined) ||
          error?.message ||
          "Failed to update withdrawal status";
        console.error("Error updating withdrawal status:", error);
        setActionError(msg);
      });
  };

  const safeTotalPages = Math.max(1, totalPages || 1);
  const showPagination = total > 0 && totalPages > 0;

  return (
    <div className="max-w-6xl mx-auto p-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">Investment Withdrawal</h1>

      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <input
          type="text"
          placeholder="Search by Name or Email..."
          className="border rounded-lg p-3 w-full md:w-1/2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <div className="flex w-full md:w-auto gap-2">
          <select
            className="border rounded-lg p-3 w-full md:w-44 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All</option>
            <option value="processing">Processing</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            className="border rounded-lg p-3 w-full md:w-40 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="20">20 / page</option>
            <option value="50">50 / page</option>
            <option value="100">100 / page</option>
          </select>
        </div>
      </div>

      <div className="space-y-6">
        {withdrawalData.length > 0 ? (
          withdrawalData.map((item, index) => (
            <div key={item._id || index} className="bg-white shadow-lg rounded-xl p-4 md:p-6 border border-gray-200 w-full hover:shadow-xl transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm md:text-lg font-bold text-gray-800 truncate">{item.fullName}</h2>
                  <p className="text-[10px] text-gray-400 mt-0.5 font-medium truncate">{item.email}</p>
                </div>
                <span
                  className={`shrink-0 px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-bold uppercase tracking-tight ${item.status.toLowerCase() === "approved"
                    ? "bg-green-100 text-green-700"
                    : item.status.toLowerCase() === "processing"
                      ? "bg-yellow-100 text-yellow-700"
                      : "bg-red-100 text-red-700"
                    }`}
                >
                  {item.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-y-3 gap-x-4 md:flex md:flex-col md:gap-1 text-xs md:text-sm">
                <div className="flex flex-col md:flex-row md:gap-2 gap-0.5">
                  <span className="text-[9px] md:text-sm text-gray-400 md:text-gray-600 font-bold md:font-semibold uppercase md:capitalize">Withdrawal</span>
                  <span className="text-blue-600 font-black text-sm md:text-base">${item.withdrawalAmount.toFixed(2)}</span>
                </div>
                <div className="flex flex-col md:flex-row md:gap-2 gap-0.5">
                  <span className="text-[9px] md:text-sm text-gray-400 md:text-gray-600 font-bold md:font-semibold uppercase md:capitalize">Net Credit</span>
                  <span className="text-green-600 font-bold">
                    {item.finalAmountCredited !== undefined ? `$${item.finalAmountCredited.toFixed(2)}` : "Pending"}
                  </span>
                </div>
                <div className="flex flex-col md:flex-row md:gap-2 gap-0.5">
                  <span className="text-[9px] md:text-sm text-gray-400 md:text-gray-600 font-bold md:font-semibold uppercase md:capitalize">Total Portfolio</span>
                  <span className="text-gray-700 font-medium">${item.totalAmount.toFixed(2)}</span>
                </div>
                <div className="flex flex-col md:flex-row md:gap-2 gap-0.5">
                  <span className="text-[9px] md:text-sm text-gray-400 md:text-gray-600 font-bold md:font-semibold uppercase md:capitalize">Mobile</span>
                  <span className="text-gray-700 font-medium truncate">{item.mobileNumber}</span>
                </div>
                <div className="flex flex-col md:flex-row md:gap-2 gap-0.5">
                  <span className="text-[9px] md:text-sm text-gray-400 md:text-gray-600 font-bold md:font-semibold uppercase md:capitalize">Investment Date</span>
                  <span className="text-gray-700 font-medium whitespace-nowrap">
                    {item.investmentDate ? new Date(item.investmentDate).toLocaleDateString('en-GB') : 'N/A'}
                  </span>
                </div>
                <div className="flex flex-col md:flex-row md:gap-2 gap-0.5">
                  <span className="text-[9px] md:text-sm text-gray-400 md:text-gray-600 font-bold md:font-semibold uppercase md:capitalize">Days Since Inv</span>
                  <span className="text-gray-700 font-medium">{item.daysSinceInvestment !== undefined ? `${item.daysSinceInvestment} days` : 'N/A'}</span>
                </div>
                <div className="col-span-1 md:hidden">
                  <span className="text-[9px] text-gray-400 font-bold uppercase block">Deduction</span>
                  <span className="text-red-500 font-bold">{(item.deductionPercentage || 0).toFixed(1)}%</span>
                </div>
              </div>

              {item.status.toLowerCase().trim() === "processing" && (
                <div className="mt-4 flex space-x-4">
                  <button
                    onClick={() => handleAction(item, "Accept")}
                    className="text-white bg-green-500 hover:bg-green-600 px-4 py-2 rounded"
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => handleAction(item, "Reject")}
                    className="text-white bg-red-500 hover:bg-red-600 px-4 py-2 rounded"
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))
        ) : (
          !isLoading && <p className="text-gray-500 text-center">No withdrawals found.</p>
        )}
      </div>

      {isLoading && withdrawalData.length === 0 ? (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : null}

      {!isLoading && showPagination && (
        <div className="flex flex-col md:flex-row justify-center items-center gap-4 mt-8 mb-4">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors font-medium text-gray-700"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600 px-2">
              Page {page} / {safeTotalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(safeTotalPages, p + 1))}
              disabled={page >= safeTotalPages}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors font-medium text-gray-700"
            >
              Next
            </button>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg border shadow-sm">
            <span className="whitespace-nowrap">of {safeTotalPages} ({total} total)</span>
          </div>
        </div>
      )}
      {showPopup && selectedWithdrawal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto shadow-2xl">
            <h2 className="text-xl font-bold mb-4">
              {actionType} Investment Withdrawal for {selectedWithdrawal.fullName}
            </h2>
            {actionError && (
              <div className="mb-3 p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm font-semibold">
                {actionError}
              </div>
            )}
            <div className="space-y-2 text-gray-600">
              <p><strong>Email:</strong> {selectedWithdrawal.email}</p>
              <p><strong>Mobile:</strong> {selectedWithdrawal.mobileNumber}</p>
              <p><strong>Referral:</strong> {selectedWithdrawal.referralName} ({selectedWithdrawal.referralMail})</p>
              <p><strong>Joining Date:</strong> {new Date(selectedWithdrawal.joiningDate).toLocaleDateString('en-GB')}</p>
              <p><strong>Investment Date:</strong> {selectedWithdrawal.investmentDate ? new Date(selectedWithdrawal.investmentDate).toLocaleDateString('en-GB') : 'N/A'}</p>
              <p><strong>Days Since Investment:</strong> {selectedWithdrawal.daysSinceInvestment !== undefined ? `${selectedWithdrawal.daysSinceInvestment} days` : 'N/A'}</p>
              <p><strong>Total Amount:</strong> ${selectedWithdrawal.totalAmount.toFixed(2)}</p>
              <p><strong>Withdrawal Amount:</strong> ${selectedWithdrawal.withdrawalAmount.toFixed(2)}</p>
              <p><strong>Subscription:</strong> {selectedWithdrawal.subscriptionPlan}</p>
              <p><strong>Status:</strong> {selectedWithdrawal.status}</p>
              <p>
                <strong>Final Amount:</strong>{" "}
                {selectedWithdrawal.finalAmountCredited !== undefined
                  ? `$${selectedWithdrawal.finalAmountCredited.toFixed(2)}`
                  : "Pending Calculation"}
              </p>
              {(selectedWithdrawal.deductionPercentage ?? 0) > 0 && (
                <div className="bg-red-50 p-3 rounded-lg border border-red-100">
                  <p className="text-red-700 font-bold mb-2 text-sm">Penalty Breakdown (FIFO Lot Match):</p>
                  <ul className="text-sm text-red-600 space-y-1">
                    {selectedWithdrawal.breakdown && selectedWithdrawal.breakdown.length > 0 ? (
                      selectedWithdrawal.breakdown.map((b, idx) => (
                        <li key={idx} className="flex justify-between border-b border-red-100 pb-1 last:border-0 last:pb-0">
                          <span>
                            ${b.amount.toFixed(2)}
                            <span className="text-xs text-red-400 ml-1">
                              ({b.daysSince} days old)
                            </span>
                          </span>
                          <span className="font-medium">
                            {b.deductionPercent}% tax = ${(b.deductionAmount).toFixed(2)}
                          </span>
                        </li>
                      ))
                    ) : (
                      <li>{(selectedWithdrawal.deductionPercentage ?? 0).toFixed(1)}% penalty applied</li>
                    )}
                  </ul>
                  <div className="mt-2 pt-2 border-t border-red-200 flex justify-between text-red-800 font-bold text-sm">
                    <span>Total Deduction:</span>
                    <span>
                      ${(
                        selectedWithdrawal.withdrawalAmount -
                        (selectedWithdrawal.finalAmountCredited ?? selectedWithdrawal.withdrawalAmount)
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                className="bg-gray-300 px-4 py-2 rounded-xl hover:bg-gray-400"
                onClick={() => setShowPopup(false)}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 rounded-xl text-white ${actionType === "Accept" ? "bg-green-600 hover:bg-green-700" : "bg-red-500 hover:bg-red-600"
                  }`}
                onClick={handleConfirmAction}
              >
                Confirm {actionType}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalDataComponent;
